import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import "./index.css";
import { requestGeolocation, startHeightSync, trackEvent, fuelAction } from "./iframe-bridge";

interface Station {
  brand: string;
  muni: string;
  addr: string;
  report_date: string;
  p95: number | null;
  diesel: number | null;
  lpg: number | null;
  lat: number | null;
  lon: number | null;
}

type FuelAvg = { p95: number | null; diesel: number | null; lpg: number | null };

interface StationData {
  stations: Station[];
  brands: string[];
  municipalities: string[];
  reportDate: string;
  updatedAt: string;
  prevAvg: FuelAvg | null;
  prevMuniAvg: Record<string, FuelAvg> | null;
}

type Fuel = "p95" | "diesel" | "lpg";

const FUEL_LABELS: Record<Fuel, string> = {
  p95: "95 benzinas",
  diesel: "Dyzelinas",
  lpg: "Dujos",
};

const TOP_CITIES = [
  "Vilniaus m. sav.",
  "Kauno m. sav.",
  "Klaipėdos m. sav.",
  "Šiaulių m. sav.",
  "Panevėžio m. sav.",
];

const fmt = (n: number) => n.toFixed(3).replace(".", ",");

function haversineKm(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

const formatDate = (iso: string) => {
  const d = new Date(iso);
  return d.toLocaleDateString("lt-LT", { year: "numeric", month: "2-digit", day: "2-digit", timeZone: "Europe/Vilnius" });
};

// Find nearest municipality to given coordinates
function findNearestMuni(lat: number, lon: number, stations: Station[], municipalities: string[]): string {
  let bestMuni = municipalities[0];
  let bestDist = Infinity;
  for (const s of stations) {
    if (s.lat == null || s.lon == null) continue;
    const d = haversineKm(lat, lon, s.lat, s.lon);
    if (d < bestDist) {
      bestDist = d;
      bestMuni = s.muni;
    }
  }
  return bestMuni;
}

export function App() {
  const [data, setData] = useState<StationData | null>(null);
  const [fuel, setFuel] = useState<Fuel>("p95");
  const [city, setCity] = useState<string | null>("Vilniaus m. sav.");
  const [activeBrands, setActiveBrands] = useState<Set<string>>(new Set());
  const [sortKey, setSortKey] = useState("price-asc");
  const [expanded, setExpanded] = useState<string | null>(null);
  const [visibleCount, setVisibleCount] = useState(20);
  const [locLoading, setLocLoading] = useState(false);
  const [locError, setLocError] = useState<string | null>(null);

  // City modal
  const [cityModalOpen, setCityModalOpen] = useState(false);
  const [citySearch, setCitySearch] = useState("");
  const citySearchRef = useRef<HTMLInputElement>(null);

  // Brand dropdown
  const [brandDropdownOpen, setBrandDropdownOpen] = useState(false);
  const [brandSearch, setBrandSearch] = useState("");
  const brandDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetch("/data/stations.json")
      .then(r => r.json())
      .then((d: StationData) => {
        setData(d);
      });
  }, []);

  useEffect(() => startHeightSync(), []);

  // Read fuel/city from URL on mount
  useEffect(() => {
    const p = new URLSearchParams(location.search);
    const f = p.get("fuel");
    if (f && f in FUEL_LABELS) setFuel(f as Fuel);
    const c = p.get("city");
    if (c === "") {
      setCity(null);
    } else if (c && data?.municipalities.some(m => m.toLowerCase() === c.toLowerCase())) {
      setCity(data.municipalities.find(m => m.toLowerCase() === c.toLowerCase())!);
    }
  }, [data]);

  // Write fuel/city to URL
  useEffect(() => {
    const p = new URLSearchParams();
    p.set("fuel", fuel);
    p.set("city", city ?? "");
    history.replaceState(null, "", "?" + p.toString());
  }, [fuel, city]);

  // Close brand dropdown on outside click
  useEffect(() => {
    if (!brandDropdownOpen) return;
    const handler = (e: MouseEvent) => {
      if (brandDropdownRef.current && !brandDropdownRef.current.contains(e.target as Node)) {
        setBrandDropdownOpen(false);
      }
    };
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, [brandDropdownOpen]);

  // Focus city search when modal opens
  useEffect(() => {
    if (cityModalOpen) {
      setTimeout(() => citySearchRef.current?.focus(), 50);
    }
  }, [cityModalOpen]);

  const stationsWithFuel = useMemo(() => {
    if (!data) return [];
    return data.stations.filter(s => s[fuel] != null);
  }, [data, fuel]);

  // Headline: compute averages for selected city and national
  const headline = useMemo(() => {
    if (stationsWithFuel.length === 0) return null;

    const natSum = stationsWithFuel.reduce((t, s) => t + s[fuel]!, 0);
    const natAvg = natSum / stationsWithFuel.length;

    if (!city) {
      // No city selected — show national average
      const min = Math.min(...stationsWithFuel.map(s => s[fuel]!));
      const max = Math.max(...stationsWithFuel.map(s => s[fuel]!));
      const prevNatAvg = data?.prevAvg?.[fuel] ?? null;
      const delta = prevNatAvg != null ? natAvg - prevNatAvg : null;
      return { avg: natAvg, min, max, count: stationsWithFuel.length, natAvg, delta, cityName: null as string | null };
    }

    const local = stationsWithFuel.filter(s => s.muni === city);
    if (local.length === 0) {
      const prevNatAvg = data?.prevAvg?.[fuel] ?? null;
      const delta = prevNatAvg != null ? natAvg - prevNatAvg : null;
      return { avg: null as number | null, min: null as number | null, max: null as number | null, count: 0, natAvg, delta, cityName: city };
    }

    const localSum = local.reduce((t, s) => t + s[fuel]!, 0);
    const localAvg = localSum / local.length;
    const localMin = Math.min(...local.map(s => s[fuel]!));
    const localMax = Math.max(...local.map(s => s[fuel]!));

    const prevLocalAvg = data?.prevMuniAvg?.[city]?.[fuel] ?? null;
    const delta = prevLocalAvg != null ? localAvg - prevLocalAvg : null;

    return { avg: localAvg, min: localMin, max: localMax, count: local.length, natAvg, delta, cityName: city };
  }, [stationsWithFuel, fuel, city, data]);

  const requestLocation = useCallback(() => {
    trackEvent("page", "filter_location");
    setLocLoading(true);
    setLocError(null);
    requestGeolocation(
      (coords) => {
        if (data) {
          const nearest = findNearestMuni(coords.latitude, coords.longitude, data.stations, data.municipalities);
          setCity(nearest);
        }
        setLocLoading(false);
      },
      (msg) => {
        setLocError(msg);
        setLocLoading(false);
      },
    );
  }, [data]);

  // Filter stations by city and brands
  const filteredStations = useMemo(() => {
    let list = stationsWithFuel;
    if (city) list = list.filter(s => s.muni === city);
    if (activeBrands.size > 0) list = list.filter(s => activeBrands.has(s.brand));

    const sorters: Record<string, (a: Station, b: Station) => number> = {
      "price-asc": (a, b) => a[fuel]! - b[fuel]!,
      "price-desc": (a, b) => b[fuel]! - a[fuel]!,
      "brand": (a, b) => a.brand.localeCompare(b.brand, "lt"),
    };
    list.sort(sorters[sortKey] || sorters["price-asc"]);
    return list;
  }, [stationsWithFuel, city, activeBrands, sortKey, fuel]);

  // Total unfiltered count for the city (before brand filter)
  const totalInCity = useMemo(() => {
    if (!city) return stationsWithFuel.length;
    return stationsWithFuel.filter(s => s.muni === city).length;
  }, [stationsWithFuel, city]);

  useEffect(() => { setVisibleCount(20); }, [activeBrands, sortKey, fuel, city]);

  const toggleBrand = useCallback((brand: string) => {
    trackEvent("page", "filter_station");
    setActiveBrands(prev => {
      const next = new Set(prev);
      if (next.has(brand)) next.delete(brand);
      else next.add(brand);
      return next;
    });
  }, []);

  const clearBrands = useCallback(() => {
    setActiveBrands(new Set());
  }, []);

  const toggleExpand = useCallback((key: string) => {
    setExpanded(prev => prev === key ? null : key);
  }, []);

  // City modal helpers
  const openCityModal = useCallback(() => {
    setCitySearch("");
    setCityModalOpen(true);
  }, []);

  const closeCityModal = useCallback(() => {
    setCityModalOpen(false);
  }, []);

  const selectCity = useCallback((c: string | null) => {
    if (c) trackEvent("page", "filter_municipality");
    setCity(c);
    closeCityModal();
  }, [closeCityModal]);

  // Filter municipalities for city modal
  const filteredMunis = useMemo(() => {
    if (!data) return { top: [], rest: [] };
    const q = citySearch.toLowerCase().trim();
    const matches = (m: string) => !q || m.toLowerCase().includes(q);

    const top = TOP_CITIES.filter(m => data.municipalities.includes(m)).filter(matches);
    const rest = data.municipalities
      .filter(m => !TOP_CITIES.includes(m))
      .filter(matches)
      .sort((a, b) => a.localeCompare(b, "lt"));

    return { top, rest };
  }, [data, citySearch]);

  // Brand dropdown label
  const brandBtnLabel = useMemo(() => {
    if (activeBrands.size === 0) return "Visi tinklai";
    if (activeBrands.size === 1) return [...activeBrands][0];
    return `Tinklai: ${activeBrands.size}`;
  }, [activeBrands]);

  // Filtered brands for dropdown search
  const filteredBrands = useMemo(() => {
    if (!data) return [];
    const q = brandSearch.toLowerCase().trim();
    if (!q) return data.brands;
    return data.brands.filter(b => b.toLowerCase().includes(q));
  }, [data, brandSearch]);

  // City display name (short)
  const cityDisplayName = (muni: string) => {
    return muni.replace(/ m\. sav\.$/, "").replace(/ r\. sav\.$/, " r.");
  };

  if (!data) {
    return (
      <div className="max-w-[960px] mx-auto px-4 py-16 text-center text-grey-60">
        Kraunama...
      </div>
    );
  }

  return (
    <div className="font-body">
      <main className="max-w-[960px] mx-auto px-4">
        {/* Page title */}
        <div className="pt-8 pb-2">
          <h1 className="font-display font-bold text-[clamp(32px,6vw,52px)] leading-[0.95] tracking-tight m-0 mb-2">
            Degalų kainos Lietuvoje
          </h1>
        </div>

        {/* Meta bar */}
        <div className="text-sm text-grey-60 py-3 border-t border-b border-grey-20 mt-4 flex flex-wrap justify-between gap-y-2">
          <span>Duomenys atnaujinti: <b className="text-white">{formatDate(data.reportDate)} 10:00</b></span>
          <span>Atnaujinama kasdien</span>
          <span>Duomenys iš <a href="https://www.ena.lt/" target="_blank" rel="noopener" className="underline text-grey-60 hover:text-white">LEA</a></span>
          <span>Kainos gali keistis degalinėse</span>
        </div>

        {/* Fuel picker */}
        <div className="flex gap-2 my-6" role="tablist" aria-label="Pasirinkite kuro rūšį">
          {(["p95", "diesel", "lpg"] as Fuel[]).map(f => (
            <button
              key={f}
              role="tab"
              aria-selected={fuel === f}
              onClick={() => { trackEvent("page", fuelAction(f)); setFuel(f); }}
              className={`flex-1 py-3.5 px-2 border-2 border-white font-semibold text-[15px] cursor-pointer transition-all duration-150
                ${fuel === f ? "bg-white text-black" : "bg-transparent text-white hover:bg-grey-10"}`}
            >
              {FUEL_LABELS[f]}
            </button>
          ))}
        </div>

        {/* Headline */}
        {headline && (
          <section className="bg-[#047FFE] text-white px-6 pt-7 pb-8 mb-8">
            <div className="text-[13px] font-medium uppercase tracking-wider opacity-85 mb-1">
              {FUEL_LABELS[fuel]}{headline.cityName ? ` · ${headline.cityName}` : ""}
            </div>
            <div className="flex items-end justify-between gap-4 flex-wrap my-1">
              <div className="font-display font-bold text-[clamp(64px,14vw,128px)] leading-[0.9] tracking-tight">
                {headline.avg != null ? (
                  <>
                    {fmt(headline.avg)}
                    <span className="text-[0.4em] font-bold opacity-75"> €/l vidurkis</span>
                  </>
                ) : (
                  <span>—</span>
                )}
              </div>
              {headline.delta != null && Math.abs(headline.delta) >= 0.0005 && (
                <span className={`font-body font-bold text-[15px] py-2 px-4 rounded-full whitespace-nowrap inline-flex items-center gap-1.5 mb-2
                  max-sm:text-[13px] max-sm:py-1.5 max-sm:px-3
                  ${headline.delta > 0
                    ? "bg-[#ffe539] text-black"
                    : "bg-white/20 text-white border border-white/40"}`}
                >
                  {headline.delta > 0 ? "↑" : "↓"} {fmt(Math.abs(headline.delta))} € nei vakar
                </span>
              )}
            </div>
            {headline.avg == null && headline.cityName && (
              <div className="text-lg font-semibold">Nėra duomenų šioje savivaldybėje</div>
            )}
            <div className="mt-5 pt-4 border-t border-white/30 flex flex-wrap gap-6 text-sm max-sm:flex-col max-sm:gap-2.5">
              {headline.min != null && (
                <span>Pigiausia: <b>{fmt(headline.min)}</b></span>
              )}
              {headline.max != null && (
                <span>Brangiausia: <b>{fmt(headline.max)}</b></span>
              )}
              {city && (
                <span>Lietuvoje vidurkis: <b>{fmt(headline.natAvg)} €/l</b></span>
              )}
            </div>
          </section>
        )}

        {/* Station list section */}
        <section>
          <div className="flex justify-between items-end gap-3 mb-4 flex-wrap max-sm:flex-col max-sm:items-stretch">
            <h2 className="font-display font-bold text-[28px] m-0 leading-none">
              {city ? `Degalinės ${city}` : "Visos degalinės"}
            </h2>
            <div className="flex gap-2 items-center max-sm:justify-end">
              <button
                onClick={openCityModal}
                className="font-body text-sm font-semibold py-2.5 px-4 pr-9 bg-white text-black border border-white rounded-full cursor-pointer
                  bg-[url('data:image/svg+xml;utf8,<svg%20xmlns=%22http://www.w3.org/2000/svg%22%20width=%2212%22%20height=%228%22%20viewBox=%220%200%2012%208%22><path%20fill=%22black%22%20d=%22M6%208L0%200h12z%22/></svg>')]
                  bg-no-repeat bg-[position:right_14px_center] hover:bg-[#e5e5e5]"
              >
                {city ? cityDisplayName(city) : "Lietuva"}
              </button>
              <button
                onClick={requestLocation}
                disabled={locLoading}
                className="font-body text-sm font-semibold py-2.5 px-4 bg-black text-white border border-[#555] rounded-full cursor-pointer hover:border-white disabled:opacity-60"
              >
                {locLoading ? "..." : "📍"}
              </button>
            </div>
          </div>

          {locError && (
            <div className="text-[13px] text-red-500 mb-3">{locError}</div>
          )}

          {/* Filters row */}
          <div className="border-t border-b border-[#222] py-3.5 mb-4 flex gap-3 items-center flex-wrap">
            {/* Brand multi-select dropdown */}
            <div className="relative" ref={brandDropdownRef}>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setBrandDropdownOpen(prev => !prev);
                  setBrandSearch("");
                }}
                className="font-body text-[13px] font-semibold py-2.5 px-4 pr-9 bg-black text-white border border-[#555] rounded-full cursor-pointer min-w-[200px] text-left
                  bg-[url('data:image/svg+xml;utf8,<svg%20xmlns=%22http://www.w3.org/2000/svg%22%20width=%2212%22%20height=%228%22%20viewBox=%220%200%2012%208%22><path%20fill=%22white%22%20d=%22M6%208L0%200h12z%22/></svg>')]
                  bg-no-repeat bg-[position:right_14px_center] hover:border-white
                  max-sm:min-w-0"
              >
                {brandBtnLabel}
              </button>
              {brandDropdownOpen && (
                <div className="absolute top-[calc(100%+6px)] left-0 bg-[#111] border border-[#444] min-w-[260px] max-h-[360px] z-[100] flex flex-col">
                  <div className="flex justify-between px-3.5 py-2.5 border-b border-[#333]">
                    <span className="text-xs font-semibold uppercase tracking-wide text-grey-60">Tinklai</span>
                    <button
                      onClick={clearBrands}
                      className={`text-xs bg-transparent border-none cursor-pointer underline
                        ${activeBrands.size === 0 ? "text-[#555] pointer-events-none" : "text-[#b3b3b3] hover:text-white"}`}
                    >
                      Išvalyti
                    </button>
                  </div>
                  <input
                    type="text"
                    value={brandSearch}
                    onChange={e => setBrandSearch(e.target.value)}
                    placeholder="Ieškoti tinklo…"
                    className="mx-2.5 my-2 px-3 py-2 bg-black border border-[#444] text-white text-[13px] font-body focus:outline-none focus:border-[#047FFE]"
                    autoComplete="off"
                  />
                  <div className="overflow-y-auto py-1.5">
                    {filteredBrands.map(b => (
                      <label
                        key={b}
                        className="flex items-center gap-2.5 px-3.5 py-2 cursor-pointer text-[13px] hover:bg-[#1a1a1a]"
                      >
                        <input
                          type="checkbox"
                          checked={activeBrands.has(b)}
                          onChange={() => toggleBrand(b)}
                          className="accent-[#047FFE]"
                        />
                        <span>{b}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Sort select */}
            <select
              value={sortKey}
              onChange={e => { trackEvent("page", "sort"); setSortKey(e.target.value); }}
              className="font-body text-[13px] font-semibold py-2.5 px-4 pr-9 bg-black text-white border border-[#555] rounded-full cursor-pointer
                appearance-none
                bg-[url('data:image/svg+xml;utf8,<svg%20xmlns=%22http://www.w3.org/2000/svg%22%20width=%2212%22%20height=%228%22%20viewBox=%220%200%2012%208%22><path%20fill=%22white%22%20d=%22M6%208L0%200h12z%22/></svg>')]
                bg-no-repeat bg-[position:right_14px_center] hover:border-white focus:outline-none focus:border-[#047FFE]
                ml-auto"
            >
              <option value="price-asc">↑ Kaina</option>
              <option value="price-desc">↓ Kaina</option>
              <option value="brand">Tinklas A–Ž</option>
            </select>
          </div>

          {/* Station list */}
          <StationList
            stations={filteredStations.slice(0, visibleCount)}
            fuel={fuel}
            expanded={expanded}
            onToggle={toggleExpand}
            emptyMessage={
              city
                ? "Pagal šiuos filtrus degalinių nerasta. Pabandyk pridėti tinklų arba pasirinkti kitą miestą."
                : "Pagal šiuos filtrus degalinių nerasta."
            }
          />

          {/* Count line */}
          {filteredStations.length > 0 && (
            <div className="text-[13px] text-[#888] text-center mt-4">
              Rodoma <b className="text-white">{Math.min(visibleCount, filteredStations.length)}</b> iš <b className="text-white">{filteredStations.length}</b> degalinių{city ? ` ${city}` : ""}
              {filteredStations.length !== totalInCity && city && ` (iš viso ${totalInCity})`}
            </div>
          )}

          {/* Load more */}
          {visibleCount < filteredStations.length && (
            <button
              onClick={() => { trackEvent("page", "load_more"); setVisibleCount(prev => prev + 20); }}
              className="w-full py-[18px] bg-grey-10 border-none font-body font-semibold text-[15px] cursor-pointer text-center mt-4 mb-4 hover:bg-grey-20 transition-colors"
            >
              Rodyti daugiau ({Math.min(20, filteredStations.length - visibleCount)}) ↓
            </button>
          )}
        </section>

        {/* Transparency */}
        <section className="bg-grey-10 p-6 mt-12 mb-8">
          <h3 className="font-display font-bold text-xl m-0 mb-3">Apie duomenis</h3>
          <p className="text-[13px] text-grey-80 my-2">
            <b>Duomenų šaltinis:</b> Lietuvos energetikos agentūra (LEA). Pirminiai šaltiniai: degalinių tinklus valdančios įmonės.
          </p>
          <p className="text-[13px] text-grey-80 my-2">
            Nuo 2026 m. balandžio kiekvienas degalinių tinklas privalo kasdien teikti kainas LEA.
          </p>
          <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-[13px]">
            <a href="/history" className="text-[#047FFE] no-underline hover:underline">Kainų istorija →</a>
            <a href="/distribution" className="text-[#047FFE] no-underline hover:underline">Kainų pasiskirstymas →</a>
          </div>
        </section>
      </main>

      {/* City picker modal */}
      {cityModalOpen && (
        <div
          className="fixed inset-0 bg-black/60 z-[1000] flex items-start justify-center pt-[10vh]"
          onClick={closeCityModal}
          onKeyDown={e => { if (e.key === "Escape") closeCityModal(); }}
        >
          <div
            className="bg-[#111] border border-[#333] w-full max-w-[400px] max-h-[70vh] flex flex-col mx-4"
            onClick={e => e.stopPropagation()}
          >
            <header className="flex justify-between items-center px-4 py-3 border-b border-[#333]">
              <h3 className="font-display font-bold text-lg m-0">Pasirink miestą</h3>
              <button
                onClick={closeCityModal}
                className="bg-transparent border-none text-white text-2xl cursor-pointer px-2 leading-none hover:opacity-70"
                aria-label="Uždaryti"
              >
                ×
              </button>
            </header>
            <input
              ref={citySearchRef}
              type="text"
              value={citySearch}
              onChange={e => setCitySearch(e.target.value)}
              placeholder="Ieškoti miesto…"
              className="mx-4 my-3 px-3 py-2.5 bg-black border border-[#444] text-white text-sm font-body focus:outline-none focus:border-[#047FFE]"
              autoComplete="off"
            />
            <div className="overflow-y-auto flex-1 pb-2">
              {filteredMunis.top.length > 0 && !citySearch && (
                <div className="px-4 py-2 text-xs font-semibold uppercase tracking-wide text-grey-60">
                  Didieji miestai
                </div>
              )}
              {filteredMunis.top.map(m => (
                <button
                  key={m}
                  onClick={() => selectCity(m)}
                  className={`w-full text-left px-4 py-3 bg-transparent border-none text-white text-sm font-body cursor-pointer hover:bg-[#1a1a1a] transition-colors
                    ${city === m ? "font-semibold bg-[#1a1a1a]" : ""}`}
                >
                  {m}
                </button>
              ))}
              {filteredMunis.rest.length > 0 && (
                <div className="px-4 py-2 text-xs font-semibold uppercase tracking-wide text-grey-60">
                  {citySearch ? "Rezultatai" : "Visos savivaldybės"}
                </div>
              )}
              {filteredMunis.rest.map(m => (
                <button
                  key={m}
                  onClick={() => selectCity(m)}
                  className={`w-full text-left px-4 py-3 bg-transparent border-none text-white text-sm font-body cursor-pointer hover:bg-[#1a1a1a] transition-colors
                    ${city === m ? "font-semibold bg-[#1a1a1a]" : ""}`}
                >
                  {m}
                </button>
              ))}
              {filteredMunis.top.length === 0 && filteredMunis.rest.length === 0 && (
                <div className="px-4 py-3 text-sm text-grey-60">Nieko nerasta</div>
              )}
              {city && (
                <button
                  onClick={() => selectCity(null)}
                  className="w-full text-left px-4 py-3 bg-transparent border-none text-[#047FFE] text-sm font-body cursor-pointer hover:bg-[#1a1a1a] transition-colors border-t border-[#333] mt-2"
                >
                  ← Rodyti Lietuvos vidurkį
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StationList({ stations, fuel, expanded, onToggle, emptyMessage }: {
  stations: Station[];
  fuel: Fuel;
  expanded: string | null;
  onToggle: (key: string) => void;
  emptyMessage: string;
}) {
  if (stations.length === 0) {
    return <div className="py-8 text-center text-grey-60 text-sm">{emptyMessage}</div>;
  }

  return (
    <ul className="list-none p-0 m-0 mb-8 border-t border-grey-20">
      {stations.map((s) => {
        const key = `${s.brand}|${s.addr}`;
        const isExpanded = expanded === key;
        const price = s[fuel]!;
        const mapsURL = `https://maps.google.com/?q=${encodeURIComponent(s.addr + ", " + s.muni)}`;

        return (
          <li
            key={key}
            onClick={() => onToggle(key)}
            className="border-b border-grey-20 cursor-pointer transition-colors hover:bg-grey-10 rounded py-4 px-4 grid grid-cols-[1fr_auto] gap-x-4 gap-y-1 items-center"
          >
            <div className="font-semibold text-base">{s.brand}</div>
            <div className="row-span-2 font-display font-bold text-[28px] text-right leading-none whitespace-nowrap">
              {fmt(price)}
              <span className="text-[13px] font-semibold text-grey-60 block mt-0.5">€/l</span>
            </div>
            <div className="text-[13px] text-grey-60">
              {s.addr.includes(",") ? s.addr : `${s.muni}, ${s.addr}`}
            </div>

            {isExpanded && (
              <div className="col-span-2 mt-2.5 flex flex-wrap gap-2 items-center">
                <FuelChip label="95" value={s.p95} />
                <FuelChip label="Dyzelinas" value={s.diesel} />
                <FuelChip label="Dujos" value={s.lpg} />
                <a
                  href={mapsURL}
                  target="_blank"
                  rel="noopener"
                  onClick={e => e.stopPropagation()}
                  className="inline-flex items-center gap-1.5 bg-[#ffe539] text-black no-underline py-2 px-4 text-[13px] font-semibold border border-black rounded-full ml-auto hover:bg-black hover:text-[#ffe539] transition-colors"
                >
                  Maršrutas →
                </a>
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}

function FuelChip({ label, value }: { label: string; value: number | null }) {
  if (value == null) return null;
  return (
    <span className="text-xs py-1.5 px-2.5 border border-grey-20 bg-grey-10 font-medium">
      {label}: <b className="font-display font-bold">{fmt(value)}</b>
    </span>
  );
}

export default App;
